import { mix, pickReadable, withAlpha } from './color'
import { dark } from './dark'
import { SemanticColors, semanticFromPalette } from './semantic'

/**
 * Cores-base de um tema de escola. O resto dos papéis é derivado delas; o que a
 * derivação não acertar entra em `overrides`.
 */
export interface IThemeSeed {
  /** Fundo da página. */
  background: string
  /** Cards, painéis, header e modais. */
  surface: string
  /** Hover, item selecionado e bloco dentro de card. Derivado se ausente. */
  surfaceRaised?: string
  /** Borda de card e input; também o trilho de progresso. */
  border: string
  /** Texto principal. */
  text: string
  /** Links, ícones de ação, aba ativa, foco. */
  accent: string
  /** Botão de compra/CTA. */
  cta: string
  /** Preenchimento da barra de progresso. Usa `accent` se ausente. */
  progress?: string
  overrides?: Partial<SemanticColors>
}

const defaultSemantic = semanticFromPalette(dark)

export function buildTheme(seed: IThemeSeed): SemanticColors {
  const { background, surface, border, text, accent, cta } = seed
  const surfaceRaised = seed.surfaceRaised ?? mix(surface, border, 0.5)
  const towardsBackground = (amount: number) => mix(text, background, amount)

  const derived: SemanticColors = {
    'bg-page': background,
    'bg-chrome': surface,
    'bg-surface': surface,
    'bg-surface-sunken': background,
    'bg-surface-raised': surfaceRaised,
    'bg-surface-pressed': mix(surfaceRaised, text, 0.12),
    'bg-inverse': text,
    'bg-surface-strong': mix(surfaceRaised, text, 0.12),
    'bg-accent-soft': mix(surface, accent, 0.25),
    'bg-input': surface,
    'bg-disabled': mix(surface, text, 0.18),
    'bg-tooltip': text,
    'bg-overlay': withAlpha(background, 0.72),
    'bg-locked': accent,
    'skeleton-base': surfaceRaised,
    'skeleton-highlight': mix(surface, text, 0.45),
    'border-subtle': mix(surface, border, 0.5),
    'border-default': border,
    'border-strong': mix(border, text, 0.25),
    'border-muted': towardsBackground(0.36),
    'border-hover': text,
    'border-focus': accent,
    'text-primary': text,
    'text-body': towardsBackground(0.05),
    'text-secondary': towardsBackground(0.24),
    'text-muted': towardsBackground(0.36),
    'text-placeholder': towardsBackground(0.45),
    'text-disabled': towardsBackground(0.5),
    'text-on-accent': pickReadable(accent, [background, text]),
    'text-on-cta': pickReadable(cta, [background, text]),
    'text-on-tooltip': pickReadable(text, [background, surface]),
    'text-on-media': defaultSemantic['text-on-media'],
    'text-on-locked': pickReadable(accent, [background, text]),
    'icon-primary': text,
    'icon-secondary': towardsBackground(0.24),
    'icon-default': towardsBackground(0.68),
    'icon-soft': towardsBackground(0.05),
    'icon-tertiary': towardsBackground(0.6),
    accent,
    'accent-hover': mix(accent, background, 0.2),
    'accent-pressed': mix(accent, text, 0.3),
    'accent-subtle': mix(background, accent, 0.35),
    cta,
    'cta-hover': mix(cta, text, 0.2),
    'cta-pressed': mix(cta, background, 0.25),
    spinner: accent,
    'spinner-strong': accent,
    'scrollbar-thumb': accent,
    'control-inactive': towardsBackground(0.6),
    'control-active': towardsBackground(0.24),
    'control-border': towardsBackground(0.45),
    'control-track-off': towardsBackground(0.36),
    'control-track-off-hover': towardsBackground(0.24),
    'progress-track': border,
    'progress-fill': seed.progress ?? accent,
    'progress-complete': defaultSemantic['progress-complete'],
  }

  return { ...derived, ...seed.overrides }
}

export const SCHOOL_THEME_PREFIX = 'school'

export function schoolThemeName(seed: IThemeSeed): string {
  const serialized = JSON.stringify(seed)
  let hash = 0
  for (let index = 0; index < serialized.length; index++) {
    hash = (hash * 31 + serialized.charCodeAt(index)) | 0
  }

  return `${SCHOOL_THEME_PREFIX}${(hash >>> 0).toString(36)}`
}

/** Tema Tamagui completo: paleta e papéis do dark com os papéis da escola por cima. */
export function schoolThemeValues(seed: IThemeSeed): Record<string, string> {
  return { ...dark, ...buildTheme(seed) }
}
